import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FormStepper from "../../../components/common/FormStepper";
import FormRenderer from "../../../components/common/FormRenderer";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";


export default function EditTemplate({
  title,
  schema,
  fetchFn,
  updateFn,
  redirectPath,
}) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchFn(id).then((res) => setValues(res.data));
  }, [id, fetchFn]);

  if (!values) return null;

  const next = () => setStep((s) => Math.min(s + 1, schema.steps.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  // The previous implementation had no try/catch — an updateFn that
  // rejected (validation 400, auth 403, etc.) left saving=true forever,
  // disabling the Update button. That's the "Update button is not
  // working" report — clicks did register, the API just rejected
  // silently. Now we surface the actual error and reset saving so the
  // operator can fix and retry without a hard refresh.
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await updateFn(id, values);
      navigate(redirectPath);
    } catch (err) {
      const data = err?.response?.data;
      let msg = "Update failed";
      if (data?.detail) {
        msg = data.detail;
      } else if (data && typeof data === "object") {
        msg = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join("; ");
      } else if (err?.message) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // 2026-06-08 — explicit Back affordance per navigation audit. The old
  // form had only Previous / Next / Update buttons; a user who tapped
  // Edit by mistake had no clear exit. navigate(redirectPath) goes
  // back to the list view; no dirty-check because this is an EDIT
  // (existing record), not an Add — leaving means abandoning edits
  // that aren't auto-saved, but the data on disk is untouched.
  const cancelEdit = () => navigate(redirectPath);

  return (
    <div>
      <button
        onClick={cancelEdit}
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-3"
        aria-label="Back to list"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="opacity-50 text-xl">{title}</h2>
      <div className="pb-16 bg-bg-primary mt-8 rounded-lg">
        <FormStepper steps={schema.steps} activeStep={step} />

        <div className="flex flex-col justify-between min-h-96 ">
          <FormRenderer
            step={schema.steps[step]}
            values={values}
            onChange={(k, v) => setValues((prev) => ({ ...prev, [k]: v }))}
          />

          {error && (
            <div className="mx-8 mt-4 bg-rose-500/10 border border-rose-500/40 text-rose-300 text-sm rounded px-3 py-2 whitespace-pre-wrap">
              {error}
            </div>
          )}

          {/* Footer — same responsive shape as AddTemplate: stack on
              phone, 44px touch targets via py-3, dense layout on sm+. */}
          <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-4 sm:justify-end px-3 sm:px-8">
            <button
              onClick={cancelEdit}
              className="flex-1 sm:flex-initial bg-bg-secondary text-text-secondary border border-border-default hover:text-text-primary px-4 sm:px-8 py-3 sm:py-2 rounded"
            >
              Cancel
            </button>
            {step > 0 && (
              <button
                onClick={prev}
                className="flex-1 sm:flex-initial bg-input-secondary px-4 sm:px-8 py-3 sm:py-2 rounded flex items-center justify-center gap-2 sm:gap-4"
              >
                <ChevronLeft />
                Previous
              </button>
            )}
            {step < schema.steps.length - 1 && (
              <button
                onClick={next}
                className="flex-1 sm:flex-initial bg-input-secondary px-4 sm:px-8 py-3 sm:py-2 rounded flex items-center justify-center gap-2 sm:gap-4"
              >
                Next
                <ChevronRight />
              </button>
            )}
            {step === schema.steps.length - 1 && (
              <button
                disabled={saving}
                onClick={save}
                className="flex-1 sm:flex-initial bg-button-primary px-4 sm:px-8 py-3 sm:py-2 rounded disabled:opacity-50"
              >
                {saving ? "Updating…" : "Update"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
